"use client";

import { motion } from "motion/react";
import { ChevronRight, Link2, Pencil } from "lucide-react";
import { Icon } from "@iconify-icon/react";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useProfileDataStore } from "@/features/profile/tutor/store/useProfileDataStore";
import { useProfileEditDialogStore } from "@/features/profile/tutor/store/useProfileEditDialogStore";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip.tsx";
import { getLanguageFromLocale } from "@/features/profile/utils/profileUtils.ts";

export function CertificatesDisplay() {
  const { profileData } = useProfileDataStore();
  const data = profileData?.certificates_section;
  const { t } = useTranslation(["profile", "common"], {
    lng: getLanguageFromLocale(profileData?.header_section.locale_code),
  });
  const { open } = useProfileEditDialogStore();
  const [isExpanded, setIsExpanded] = useState(false);
  const containerRef = useRef<any>(null);

  const list = data ?? [];
  const displayedCertificates = isExpanded ? list : list.slice(0, 4);
  const hasMore = list.length > 4;

  useEffect(() => {
    if (isExpanded && containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: "instant", block: "start" });
    }
  }, [isExpanded]);

  if (!data) return null;

  return (
    <Card
      ref={containerRef}
      className="bg-side-accent/50 relative overflow-hidden rounded-2xl border-l-4 border-none border-l-purple-400/50 shadow-2xl backdrop-blur-xl"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute -right-10 -bottom-15 h-32 w-32 rounded-full bg-gradient-to-br from-purple-400 to-violet-600" />
        <div className="absolute -top-10 -left-10 h-28 w-32 rounded-full bg-gradient-to-tr from-violet-400 to-purple-500" />
      </div>

      <CardHeader className="relative flex flex-row items-center justify-between pb-2">
        <motion.h2
          animate={{ y: 0, opacity: 1 }}
          className="text-accent-foreground mb-2 flex w-full items-center gap-2 text-xl font-semibold"
          initial={{ y: -10, opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="flex items-center justify-center rounded-xl p-2">
            <Icon
              className="text-purple-400"
              height="1.25rem"
              icon="mdi:scroll-text-outline"
              width="1.25rem"
            />
          </div>
          {t("profile:certificatesSection.title")}
        </motion.h2>
        {data.length > 0 && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  className="bg-foreground/50 hover:bg-foreground/70 absolute top-2 right-2 rounded-xl backdrop-blur-sm"
                  size="sm"
                  variant="ghostSecondary"
                  onClick={() => open("certificates")}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="left">
                <p>{t("common:edit")}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </CardHeader>
      <CardContent className="relative space-y-6">
        {data.length === 0 && (
          <motion.div
            animate={{ y: 0, opacity: 1 }}
            className="relative h-[120px] w-full space-y-2 rounded-2xl border border-dashed border-purple-300/50 bg-purple-500/5 backdrop-blur-sm"
            initial={{ y: 10, opacity: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <Button
              className="absolute top-0 left-0 flex h-full w-full items-center rounded-2xl bg-transparent hover:bg-purple-500/10"
              onClick={() => open("certificates")}
            >
              <Pencil className="mr-3 h-6 w-6 text-purple-400" />
              <h3 className="text-accent-foreground text-lg font-medium">
                {t("profile:certificatesSection.add")}
              </h3>
            </Button>
          </motion.div>
        )}
        {displayedCertificates.map((certificate, index) => (
          <motion.div
            key={certificate.id}
            animate={{ y: 0, opacity: 1 }}
            className="space-y-2"
            initial={{ y: 10, opacity: 0 }}
            transition={{ duration: 0.3, delay: index * 0.1 }}
          >
            <div className="flex items-start space-x-2 rounded-2xl bg-transparent p-4 backdrop-blur-sm transition-colors hover:bg-purple-500/2">
              <div className="flex flex-shrink-0 items-center justify-center p-2">
                <Icon
                  className="text-purple-400"
                  height="1.25rem"
                  icon="mdi:certificate-outline"
                  width="1.25rem"
                />
              </div>
              <div className="flex-1 space-y-1">
                <h3 className="text-accent-foreground text-lg font-semibold">{certificate.name}</h3>
                <p className="text-muted-foreground text-sm">{certificate.issuing_organization}</p>
                {certificate.issue_date && (
                  <p className="text-muted-foreground flex items-center text-sm">
                    <Icon height="1rem" icon="mdi:calendar-edit-outline" width="1rem" />
                    <span className="ml-2">{certificate.issue_date}</span>
                    {certificate.expiration_date && (
                      <span className={"ml-4 flex items-center"}>
                        <Icon height="1rem" icon="mdi:calendar-remove-outline" width="1rem" />
                        <span className="ml-2">{certificate.expiration_date}</span>
                      </span>
                    )}
                  </p>
                )}
                {certificate.credential_id && (
                  <p className="text-muted-foreground text-sm">
                    <span className="text-muted-foreground mb-1 text-xs font-medium">
                      {t("profile:certificatesSection.display.credentialId")}{" "}
                    </span>
                    {certificate.credential_id}
                  </p>
                )}
                {certificate.description && (
                  <p className="text-muted-foreground text-sm">
                    <span className="text-muted-foreground mb-1 text-xs font-medium">
                      {t("profile:certificatesSection.display.description")}{" "}
                    </span>
                    {certificate.credential_id}
                  </p>
                )}
                {certificate.credential_url && (
                  <a
                    className="text-muted-foreground inline-flex items-center gap-2 rounded-2xl px-3 py-1 text-sm font-medium transition-colors hover:bg-purple-500/10"
                    href={certificate.credential_url}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    <Link2 className="h-4 w-4" />
                    {t("profile:certificatesSection.display.viewCredential")}
                  </a>
                )}
              </div>
            </div>
            {index !== data.length - 1 && <Separator className="bg-muted-foreground/20 my-4" />}
          </motion.div>
        ))}
        {hasMore && (
          <motion.div animate={{ opacity: 1 }} initial={{ opacity: 0 }} transition={{ delay: 0.2 }}>
            <Button
              className="mt-2 w-full justify-center gap-2 rounded-xl border-none bg-transparent text-purple-300 hover:bg-purple-500/15"
              variant="outline"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              <span>
                {isExpanded ? t("common:showLess") : t("common:showAll", { count: list.length })}
              </span>
              <ChevronRight
                className={`h-4 w-4 transition-transform duration-200 ${isExpanded ? "rotate-90" : ""}`}
              />
            </Button>
          </motion.div>
        )}
      </CardContent>
    </Card>
  );
}
